import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    ArrowRight, ThumbsUp, MessageSquare, Share2, Eye, Lock,
    LayoutDashboard, Inbox, PlusCircle, Search, FolderOpen, Wallet, BarChart3, Settings,
} from 'lucide-react'
import HeroInfluencerCollage from '../components/HeroInfluencerCollage'
import Footer from '../components/Footer'
import {
    AudienceSplit,
    HowItWorks,
    FAQ,
} from '../components/landing'
import { useAuth } from '../context/AuthContext'
import { useRevealAll } from '../hooks/useReveal'
import { FAQS } from '../data/faqs'
import { SectionHeading } from '../components/public'
import './preview.css'

const REGISTER_BUSINESS = '/register/business'
const REGISTER_CREATOR = '/register/influencer'

const FAQ_FILTERS = [
    { id: 'all',     label: 'All' },
    { id: 'brand',   label: 'For brands' },
    { id: 'creator', label: 'For creators' },
]

const COMPARE_ROWS = [
    ['Pricing model',      'Flat fee per post',     '$2 000/mo SaaS',   '$1 499/mo SaaS',   'Per verified interaction'],
    ['Budget protection',  'None',                  'None',              'None',             'Funds escrowed until verified'],
    ['Time to launch',     '2–4 weeks',             'Days',              'Days',             'Under 24 hours'],
    ['Platform fee',       '20–40% of spend',       'Bundled in SaaS',   'Bundled in SaaS',  '20% — included in budget'],
    ['Real-time data',     'Black-box reporting',   'Yes',               'Yes',              'Yes — live dashboard'],
    ['Verification',       'Self-reported',         'Platform APIs',     'Platform APIs',    'Platform APIs + admin review'],
    ['Minimum campaign',   '€1 000+',               '$2 000/mo',         '$1 499/mo',        'No minimum — start from €300'],
]

export default function Landing() {
    const navigate = useNavigate()
    const { user, loading: authLoading } = useAuth()
    useRevealAll()

    const [faqFilter, setFaqFilter] = useState('all')

    useEffect(() => {
        if (!authLoading && user) {
            const role = user?.role
            if (role === 'business')        navigate('/dashboard/business',   { replace: true })
            else if (role === 'influencer') navigate('/dashboard/influencer', { replace: true })
            else if (role === 'admin')      navigate('/admin',                { replace: true })
        }
    }, [user, authLoading, navigate])

    const dashboardPath =
        user?.role === 'business' ? '/dashboard/business'
        : user?.role === 'admin'  ? '/admin'
        : '/dashboard/influencer'

    const filteredFaqs =
        faqFilter === 'all'
            ? FAQS
            : FAQS.filter((f) => f.audiences.includes(faqFilter))

    const filterCounts = {
        all:     FAQS.length,
        brand:   FAQS.filter((f) => f.audiences.includes('brand')).length,
        creator: FAQS.filter((f) => f.audiences.includes('creator')).length,
    }

    return (
        <div className="preview-root">

            {/* ─── 1 · HERO ─── */}
            <div className="landing-dashboard-shell">
                <aside className="landing-preview-sidebar" aria-hidden="true">
                    <div className="sidebar__header">
                        <div style={{ fontWeight: 600, fontSize: '0.9375rem', letterSpacing: '-0.01em' }}>PostPaid</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-faint)', marginTop: '0.3rem' }}>
                            Business · Guest preview
                        </div>
                    </div>
                    <nav className="sidebar__nav">
                        <div className="sidebar__item" style={{ color: 'var(--ink-faint)' }}>
                            <Inbox size={18} />
                            Inbox
                            <span className="landing-nav-dot" />
                        </div>
                        <div className="sidebar__item sidebar__item--active">
                            <LayoutDashboard size={18} />
                            Overview
                        </div>
                        <div className="sidebar__item landing-sidebar-locked"><PlusCircle size={18} />Create Campaign</div>
                        <div className="sidebar__item landing-sidebar-locked"><Search size={18} />Discovery</div>
                        <div className="sidebar__item landing-sidebar-locked"><FolderOpen size={18} />My Campaigns</div>
                        <div className="sidebar__item landing-sidebar-locked"><Wallet size={18} />Wallet</div>
                        <div className="sidebar__item landing-sidebar-locked"><BarChart3 size={18} />Analytics</div>
                        <div className="sidebar__item landing-sidebar-locked"><Settings size={18} />Settings</div>
                    </nav>

                    <div className="preview-sidebar-earnings" role="status" aria-live="polite">
                        <span className="preview-sidebar-earnings__dot" aria-hidden="true" />
                        <div className="preview-sidebar-earnings__text">
                            <span className="preview-sidebar-earnings__amount">+€0.25</span>
                            <span className="preview-sidebar-earnings__label">Comment on @techwithluca</span>
                        </div>
                    </div>

                    <div className="landing-preview-sidebar__footer">
                        <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-faint)', textAlign: 'center', margin: 0 }}>
                            Talk to us before you commit
                        </p>
                        <Link to={REGISTER_BUSINESS} className="btn btn--primary btn--small btn--full" style={{ textDecoration: 'none', justifyContent: 'center' }}>
                            Start a campaign →
                        </Link>
                    </div>
                </aside>

                <div className="landing-dashboard-main">
                    <section className="hero" style={{ position: 'relative' }}>
                        <HeroInfluencerCollage />
                        <div className="container">
                            <div className="hero__content">
                                <span className="mono-label">postpaid · pay-per-engagement creator campaigns</span>
                                <h1 className="hero__title">Stop negotiating flat fees with creators who might not deliver.</h1>
                                <p className="hero__subtitle">
                                    Set a budget, approve creators, pay only for verified likes, comments, views, and shares. Unused budget stays yours.
                                </p>
                                <div className="hero__actions">
                                    {user ? (
                                        <Link to={dashboardPath} className="btn btn--primary btn--large">
                                            Go to Dashboard
                                            <ArrowRight size={20} />
                                        </Link>
                                    ) : (
                                        <>
                                            <Link to={REGISTER_BUSINESS} className="btn btn--primary btn--large">
                                                Start a campaign
                                                <ArrowRight size={20} />
                                            </Link>
                                            <Link to={REGISTER_CREATOR} className="btn btn--ghost btn--large">
                                                Join as a creator
                                                <ArrowRight size={20} />
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ─── 2 · TRUST STRIP ─── */}
                    <section className="section preview-trust" data-reveal>
                        <div className="container" style={{ maxWidth: 1100 }}>
                            <ul className="preview-trust__row">
                                <li>Stripe payments</li>
                                <li>Funds in escrow</li>
                                <li>Platform-API verified</li>
                                <li>Admin-reviewed engagement</li>
                                <li>GDPR compliant</li>
                            </ul>
                        </div>
                    </section>

                    {/* ─── 3 · THREE-STEP EXPLAINER ─── */}
                    <section className="section preview-section" data-reveal>
                        <div className="container" style={{ maxWidth: 960 }}>
                            <SectionHeading
                                eyebrow="how postpaid works"
                                title="Fund. Creators post. Pay only for verified engagement."
                                subtitle="Three steps. No retainers, no flat fees, no surprise invoices."
                            />
                            <div className="preview-cards">
                                {[
                                    ['Fund',                              'Pick a budget, fund it via Stripe. Money sits in escrow until creators deliver.'],
                                    ['Creators post',                     'Approve up to 150% of needed creators — primaries plus backups. Primaries post within 72h; backups activate automatically if anyone ghosts.'],
                                    ['Pay only for verified engagement',  'Every like, comment, view, share is verified by admin and platform APIs. Payouts release line-by-line. Unused budget refunds back to you.'],
                                ].map(([title, body]) => (
                                    <article key={title} className="preview-card">
                                        <h3>{title}</h3>
                                        <p>{body}</p>
                                    </article>
                                ))}
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                                <Link to="/how-it-works" className="btn btn--ghost">
                                    See the full breakdown →
                                </Link>
                            </div>
                        </div>
                    </section>

                    {/* ─── 4 · AUDIENCE SPLIT ─── */}
                    <section className="section preview-section" data-reveal>
                        <div className="container">
                            <AudienceSplit />
                        </div>
                    </section>

                    {/* ─── 5 · HOW IT WORKS DETAILED ─── */}
                    <section className="section section--black" data-reveal>
                        <div className="container">
                            <HowItWorks variant="product" />
                        </div>
                    </section>

                    {/* ─── 6 · MONEY FLOW (live escrow inspector) ─── */}
                    <section className="section preview-section preview-section--soft" data-reveal>
                        <div className="container" style={{ maxWidth: 880 }}>
                            <SectionHeading
                                eyebrow="the money flow"
                                title="Where every euro goes, in order."
                                subtitle="A peek at a live campaign — escrow status, verified interactions ticking up, payout countdown."
                            />

                            <div className="preview-window" aria-hidden="true">
                                <header className="preview-window__chrome">
                                    <div className="preview-window__dots">
                                        <span className="preview-window__dot preview-window__dot--red" />
                                        <span className="preview-window__dot preview-window__dot--yellow" />
                                        <span className="preview-window__dot preview-window__dot--green" />
                                    </div>
                                    <div className="preview-window__title">
                                        postpaid.io <span className="preview-window__crumb">/ campaign #4821 / escrow</span>
                                    </div>
                                </header>

                                <div className="preview-window__body">
                                    <div className="preview-window__budget">
                                        <div>
                                            <div className="preview-window__budget-label">Campaign budget</div>
                                            <div className="preview-window__budget-amount">
                                                <span>€247.20</span>
                                                <span className="preview-window__budget-of">/ €1,200.00</span>
                                            </div>
                                        </div>
                                        <div className="preview-window__budget-meta">
                                            <span className="preview-window__budget-pct">21% spent</span>
                                            <div className="preview-window__bar">
                                                <div className="preview-window__bar-fill" style={{ width: '21%' }} />
                                            </div>
                                        </div>
                                    </div>

                                    <ol className="preview-flow">
                                        {[
                                            { n: '01', title: 'Brand funds budget',         body: 'Stripe charge · receipt emailed',     status: 'done',    when: 'Mon · 10:14' },
                                            { n: '02', title: 'Funds locked in escrow',     body: 'Held until interactions land',         status: 'done',    when: 'Mon · 10:14' },
                                            { n: '03', title: 'Engagement verified',        body: 'Admin review + platform APIs',         status: 'live',    when: 'Now' },
                                            { n: '04', title: 'Creator earns line-by-line', body: 'Per like, comment, share, view',       status: 'pending', when: 'Tomorrow' },
                                            { n: '05', title: 'Payout to Stripe',           body: 'Within 48 hours, EUR or USD',          status: 'pending', when: 'Friday' },
                                        ].map(({ n, title, body, status, when }) => (
                                            <li key={n} className={`preview-flow__item preview-flow__item--${status}`}>
                                                <span className="preview-flow__num">{n}</span>
                                                <div className="preview-flow__main">
                                                    <div className="preview-flow__head">
                                                        <h4>{title}</h4>
                                                        <span className={`preview-flow__pill preview-flow__pill--${status}`}>
                                                            <span className="preview-flow__pill-dot" aria-hidden="true" />
                                                            {status}
                                                        </span>
                                                    </div>
                                                    <p>{body}</p>
                                                </div>
                                                <span className="preview-flow__when">{when}</span>
                                            </li>
                                        ))}
                                    </ol>

                                    <div className="preview-window__ticker">
                                        <div className="preview-window__ticker-head">
                                            <span className="preview-window__ticker-dot" aria-hidden="true" />
                                            <span className="preview-window__ticker-label">Verified interactions · last 60 seconds</span>
                                        </div>
                                        <ul className="preview-window__ticker-list">
                                            <li>
                                                <span className="preview-window__ticker-amount">+€0.25</span>
                                                <span className="preview-window__ticker-text">Like on <strong>@miaskin</strong></span>
                                                <span className="preview-window__ticker-time">12s ago</span>
                                            </li>
                                            <li>
                                                <span className="preview-window__ticker-amount">+€1.00</span>
                                                <span className="preview-window__ticker-text">Comment on <strong>@techwithluca</strong></span>
                                                <span className="preview-window__ticker-time">47s ago</span>
                                            </li>
                                            <li>
                                                <span className="preview-window__ticker-amount">+€2.00</span>
                                                <span className="preview-window__ticker-text">Share via <strong>@nomad_kate</strong></span>
                                                <span className="preview-window__ticker-time">1m ago</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ─── 7 · COMPARISON ─── */}
                    <section className="section preview-section" data-reveal>
                        <div className="container" style={{ maxWidth: 1100 }}>
                            <SectionHeading
                                eyebrow="the difference"
                                title="PostPaid vs. The Old Guard."
                                subtitle="Three lines for skim readers. The full breakdown below for the rest."
                            />
                            <div className="preview-verdict">
                                <div className="preview-verdict__row">→ Priced by engagement, not by post.</div>
                                <div className="preview-verdict__row">→ Funds escrowed until each interaction is verified.</div>
                                <div className="preview-verdict__row">→ Unused budget refunds back to you.</div>
                            </div>
                            <div className="preview-table-wrap">
                                <table className="preview-table">
                                    <thead>
                                        <tr>
                                            <th>What you're comparing</th>
                                            <th>Agency</th>
                                            <th>Aspire</th>
                                            <th>GRIN</th>
                                            <th className="preview-table__th--postpaid">PostPaid</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {COMPARE_ROWS.map((row) => (
                                            <tr key={row[0]}>
                                                <td><strong>{row[0]}</strong></td>
                                                <td>{row[1]}</td>
                                                <td>{row[2]}</td>
                                                <td>{row[3]}</td>
                                                <td className="preview-table__td--postpaid">
                                                    <span className="preview-table__tick" aria-hidden="true">✓</span>
                                                    {row[4]}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Link to="/compare" className="preview-compare-mobile-link">
                                See the full breakdown →
                            </Link>
                            <p className="preview-table__foot">
                                Competitor data sourced from publicly listed pricing pages as of 2026. Prices may vary.
                            </p>
                        </div>
                    </section>

                    {/* ─── 8 · PER-INTERACTION PRICING TILES ─── */}
                    <section className="section preview-section preview-section--soft" data-reveal>
                        <div className="container" style={{ maxWidth: 960 }}>
                            <SectionHeading
                                eyebrow="what you pay for"
                                title="Pay per interaction."
                                subtitle="Set your own prices for each type of engagement. Typical rates:"
                            />
                            <div className="preview-rates">
                                {[
                                    [<ThumbsUp key="l" size={20} />, 'Like',    '€0.10 – €1.00'],
                                    [<MessageSquare key="c" size={20} />, 'Comment', '€0.50 – €5.00'],
                                    [<Share2 key="s" size={20} />, 'Share',   '€1.00 – €10.00'],
                                    [<Eye key="v" size={20} />, 'View',    '€0.001 – €0.01'],
                                ].map(([icon, label, range]) => (
                                    <div key={label} className="preview-rate">
                                        <span className="preview-rate__icon">{icon}</span>
                                        <span className="preview-rate__label">{label}</span>
                                        <span className="preview-rate__range">{range}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ─── 9 · PRICING SUMMARY ─── */}
                    <section className="section section--black" id="pricing" data-reveal>
                        <div className="container" style={{ maxWidth: 720 }}>
                            <SectionHeading
                                eyebrow="pricing"
                                title="20% platform fee — included in your budget."
                                tone="dark"
                            />
                            <div className="preview-pricing-card">
                                <span className="preview-pricing-card__eyebrow">One simple rate</span>
                                <div className="preview-pricing-card__price">
                                    <span className="preview-pricing-card__big">20%</span>
                                </div>
                                <p className="preview-pricing-card__desc">
                                    Platform fee included in your budget · no retainer, no seat licenses.
                                </p>
                                <ul className="preview-pricing-card__features">
                                    <li>Unused budget refunds back to you</li>
                                    <li>Unlimited campaigns</li>
                                    <li>Real-time analytics</li>
                                    <li>EUR / USD payouts</li>
                                    <li>Admin-reviewed engagement</li>
                                    <li>Email &amp; chat support</li>
                                </ul>
                                <Link to="/pricing" className="btn btn--primary btn--large preview-pricing-card__cta">
                                    See the live calculator
                                    <ArrowRight size={18} />
                                </Link>
                                <div className="preview-pricing-card__trust">
                                    <Lock size={12} aria-hidden="true" />
                                    <span>Stripe · 256-bit TLS · PCI DSS Level 1</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ─── 10 · FAQ ─── */}
                    <section className="section preview-section" data-reveal>
                        <div className="container" style={{ maxWidth: 720 }}>
                            <div className="preview-faq-filter" role="tablist" aria-label="Filter FAQs by audience">
                                {FAQ_FILTERS.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={faqFilter === f.id}
                                        onClick={() => setFaqFilter(f.id)}
                                        className={`preview-faq-pill${faqFilter === f.id ? ' preview-faq-pill--active' : ''}`}
                                    >
                                        {f.label}
                                        <span className="preview-faq-pill__count">{filterCounts[f.id]}</span>
                                    </button>
                                ))}
                            </div>
                            <FAQ items={filteredFaqs} eyebrow="common questions" />
                        </div>
                    </section>

                    {/* ─── 12 · FINAL CTA ─── */}
                    <section className="section section--black" data-reveal>
                        <div className="container text-center">
                            <SectionHeading
                                eyebrow="get started"
                                title="Ready to get started?"
                                tone="dark"
                            />
                            <div className="flex gap-4 justify-center flex-wrap" style={{ marginTop: '2rem' }}>
                                {user ? (
                                    <Link to={dashboardPath} className="btn btn--secondary btn--large">
                                        Go to Dashboard
                                        <ArrowRight size={20} />
                                    </Link>
                                ) : (
                                    <>
                                        <Link to={REGISTER_BUSINESS} className="btn btn--secondary btn--large">
                                            Start a campaign →
                                        </Link>
                                        <Link to={REGISTER_CREATOR} className="btn btn--outline btn--large">
                                            Join as a creator →
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </section>

                    <Footer />
                </div>
            </div>
        </div>
    )
}
